import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import "@/App.css";
import { 
  Lightbulb, 
  Target, 
  Rocket, 
  Users, 
  GraduationCap, 
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  Clock,
  MapPin,
  Calendar,
  Monitor,
  Mail,
  ChevronDown,
  Shield,
  BookOpen,
  Briefcase,
  Sparkles,
  Star,
  MessageCircle,
  Award,
  Zap,
  Brain,
  ClipboardList,
  FileQuestion,
  PenTool,
  Download,
  FileText,
  CreditCard,
  AlertCircle,
  Info,
  Heart,
  Leaf,
  Trophy,
  Dumbbell,
  Building2,
  Cog
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import EventsListingPage from "./pages/EventsListingPage";
import EventDetailPage from "./pages/EventDetailPage";
import AdminEventsPage from "./pages/AdminEventsPage";
import SeedPartnershipPage from "./pages/SeedPartnershipPage";
import GitamBBAPage from "./pages/GitamBBAPage";
import HomePage from "./pages/HomePage";
import UGProgramPage from "./pages/UGProgramPage";
import FounderOSPage from "./pages/FounderOSPage";

const BOWER_LOGO = "https://customer-assets.emergentagent.com/job_9905ee30-c002-464f-b890-465ab6cc70dc/artifacts/9497g015_Bower%20School%20Logo-01.png";

// Images from design guidelines
const IMAGES = {
  studentsGroup: "https://customer-assets.emergentagent.com/job_4f3c92f1-dfdf-4d89-a249-d2961f0b5da8/artifacts/fw06kadd_2025-02-12.webp",
  parentsTop: "https://customer-assets.emergentagent.com/job_4f3c92f1-dfdf-4d89-a249-d2961f0b5da8/artifacts/ccm3eh0z_IMG_5509.JPG",
  parentsBottom: "https://customer-assets.emergentagent.com/job_4f3c92f1-dfdf-4d89-a249-d2961f0b5da8/artifacts/fa1g6o9y_DSC06733.JPG",
};

// Country codes data
const COUNTRY_CODES = [
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+968', country: 'Oman', flag: '🇴🇲' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
  { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
];

// Indian States and Cities Data
const INDIAN_STATES_CITIES = {
  "Andhra Pradesh": ["Visakhapatnam", "Vijayawada", "Guntur", "Nellore", "Kurnool", "Tirupati", "Rajahmundry", "Kakinada", "Kadapa", "Anantapur"],
  "Arunachal Pradesh": ["Itanagar", "Naharlagun", "Pasighat", "Tawang", "Ziro", "Bomdila", "Aalo", "Tezu", "Namsai", "Changlang"],
  "Assam": ["Guwahati", "Silchar", "Dibrugarh", "Jorhat", "Nagaon", "Tinsukia", "Tezpur", "Karimganj", "Bongaigaon", "Goalpara"],
  "Bihar": ["Patna", "Gaya", "Bhagalpur", "Muzaffarpur", "Purnia", "Darbhanga", "Bihar Sharif", "Arrah", "Begusarai", "Katihar"],
  "Chhattisgarh": ["Raipur", "Bhilai", "Bilaspur", "Korba", "Durg", "Rajnandgaon", "Raigarh", "Jagdalpur", "Ambikapur", "Dhamtari"],
  "Goa": ["Panaji", "Margao", "Vasco da Gama", "Mapusa", "Ponda", "Bicholim", "Curchorem", "Sanquelim", "Cuncolim", "Quepem"],
  "Gujarat": ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar", "Junagadh", "Gandhinagar", "Anand", "Morbi"],
  "Haryana": ["Gurugram", "Faridabad", "Panipat", "Ambala", "Yamunanagar", "Rohtak", "Hisar", "Karnal", "Sonipat", "Panchkula"],
  "Himachal Pradesh": ["Shimla", "Dharamshala", "Solan", "Mandi", "Palampur", "Baddi", "Nahan", "Kullu", "Manali", "Hamirpur"],
  "Jharkhand": ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro", "Hazaribagh", "Deoghar", "Giridih", "Ramgarh", "Phusro", "Medininagar"],
  "Karnataka": ["Bengaluru", "Mysuru", "Mangaluru", "Hubli", "Belgaum", "Gulbarga", "Davangere", "Bellary", "Shimoga", "Tumkur"],
  "Kerala": ["Thiruvananthapuram", "Kochi", "Kozhikode", "Thrissur", "Kollam", "Palakkad", "Alappuzha", "Kannur", "Kottayam", "Malappuram"],
  "Madhya Pradesh": ["Bhopal", "Indore", "Jabalpur", "Gwalior", "Ujjain", "Sagar", "Dewas", "Satna", "Ratlam", "Rewa"],
  "Maharashtra": ["Mumbai", "Pune", "Nagpur", "Thane", "Nashik", "Aurangabad", "Solapur", "Kolhapur", "Amravati", "Navi Mumbai"],
  "Manipur": ["Imphal", "Thoubal", "Bishnupur", "Churachandpur", "Kakching", "Ukhrul", "Senapati", "Tamenglong", "Chandel", "Jiribam"],
  "Meghalaya": ["Shillong", "Tura", "Jowai", "Nongstoin", "Williamnagar", "Baghmara", "Resubelpara", "Mairang", "Nongpoh", "Cherrapunji"],
  "Mizoram": ["Aizawl", "Lunglei", "Saiha", "Champhai", "Kolasib", "Serchhip", "Lawngtlai", "Mamit", "Hnahthial", "Khawzawl"],
  "Nagaland": ["Kohima", "Dimapur", "Mokokchung", "Tuensang", "Wokha", "Zunheboto", "Mon", "Phek", "Kiphire", "Longleng"],
  "Odisha": ["Bhubaneswar", "Cuttack", "Rourkela", "Berhampur", "Sambalpur", "Puri", "Balasore", "Bhadrak", "Baripada", "Jharsuguda"],
  "Punjab": ["Ludhiana", "Amritsar", "Jalandhar", "Patiala", "Bathinda", "Mohali", "Pathankot", "Hoshiarpur", "Batala", "Moga"],
  "Rajasthan": ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Bikaner", "Ajmer", "Bhilwara", "Alwar", "Sikar", "Sri Ganganagar"],
  "Sikkim": ["Gangtok", "Namchi", "Gyalshing", "Mangan", "Rangpo", "Singtam", "Jorethang", "Nayabazar", "Ravangla", "Pelling"],
  "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai", "Tiruchirappalli", "Salem", "Tirunelveli", "Tiruppur", "Vellore", "Erode", "Thoothukudi"],
  "Telangana": ["Hyderabad", "Warangal", "Nizamabad", "Karimnagar", "Khammam", "Ramagundam", "Mahbubnagar", "Nalgonda", "Adilabad", "Suryapet"],
  "Tripura": ["Agartala", "Udaipur", "Dharmanagar", "Kailashahar", "Belonia", "Khowai", "Ambassa", "Sabroom", "Sonamura", "Amarpur"],
  "Uttar Pradesh": ["Lucknow", "Kanpur", "Agra", "Varanasi", "Meerut", "Prayagraj", "Ghaziabad", "Noida", "Bareilly", "Aligarh"],
  "Uttarakhand": ["Dehradun", "Haridwar", "Roorkee", "Haldwani", "Rudrapur", "Kashipur", "Rishikesh", "Kotdwar", "Ramnagar", "Pithoragarh"],
  "West Bengal": ["Kolkata", "Howrah", "Durgapur", "Asansol", "Siliguri", "Bardhaman", "Malda", "Baharampur", "Habra", "Kharagpur"],
  "Delhi": ["New Delhi", "Central Delhi", "South Delhi", "North Delhi", "East Delhi", "West Delhi", "Dwarka", "Rohini", "Saket", "Vasant Kunj"],
  "Chandigarh": ["Chandigarh"],
  "Puducherry": ["Puducherry", "Karaikal", "Mahe", "Yanam"],
  "Jammu and Kashmir": ["Srinagar", "Jammu", "Anantnag", "Baramulla", "Udhampur", "Sopore", "Kathua", "Pulwama", "Kupwara", "Rajouri"],
  "Ladakh": ["Leh", "Kargil"]
};

const INDIAN_STATES = Object.keys(INDIAN_STATES_CITIES).sort();

// Zoho form action URL for BEAT
const BEAT_ZOHO_FORM_ACTION = "https://forms.zohopublic.in/bowereducationalservicespriv1/form/BEATTESTBBAinEntrepreneurshipInterestFormLandingPa/formperma/0N9_nEipxYJU4DwvD-e7I1-Z3lvA28D8Vnl8upnlaP8/htmlRecords/submit";

// Custom Registration Form Component
const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    countryCode: '+91',
    phone: '',
    email: '',
    schoolName: '',
    state: '',
    city: '',
    grade: '',
    stream: '',
    board: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [availableCities, setAvailableCities] = useState([]);
  const formRef = useRef(null);
  const zohoFormRef = useRef(null);

  // Update cities when state changes
  useEffect(() => {
    if (formData.state && INDIAN_STATES_CITIES[formData.state]) {
      setAvailableCities(INDIAN_STATES_CITIES[formData.state]);
      setFormData(prev => ({ ...prev, city: '' }));
    } else {
      setAvailableCities([]);
    }
  }, [formData.state]);

  const gradeOptions = [
    { value: '-Select-', label: 'Select Grade' },
    { value: 'Grade 11', label: 'Grade 11' },
    { value: 'Grade 12', label: 'Grade 12' }
  ];

  const streamOptions = [
    { value: '-Select-', label: 'Select Stream' },
    { value: 'Science', label: 'Science' },
    { value: 'Commerce', label: 'Commerce' },
    { value: 'Arts/Humanities', label: 'Arts/Humanities' }
  ];

  const boardOptions = [
    { value: '-Select-', label: 'Select Board' },
    { value: 'ICSE', label: 'ICSE' },
    { value: 'CBSE', label: 'CBSE' },
    { value: 'IB', label: 'IB' },
    { value: 'State Board', label: 'State Board' },
    { value: 'CAIE', label: 'CAIE' },
    { value: 'NIOS', label: 'NIOS' }
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'Required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Required';
    if (!formData.phone.trim()) newErrors.phone = 'Required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) newErrors.phone = 'Enter 10-digit number';
    if (!formData.email.trim()) newErrors.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email';
    if (!formData.schoolName.trim()) newErrors.schoolName = 'Required';
    if (!formData.state) newErrors.state = 'Required';
    if (!formData.city) newErrors.city = 'Required';
    if (!formData.stream || formData.stream === '-Select-') newErrors.stream = 'Required';
    if (!formData.board || formData.board === '-Select-') newErrors.board = 'Required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Get full phone number with country code
  const getFullPhoneNumber = () => {
    const cleanPhone = formData.phone.replace(/\D/g, '');
    return `${formData.countryCode}${cleanPhone}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitError('');
    
    const urlParams = new URLSearchParams(window.location.search);
    const getUTMValue = (p) => urlParams.get(p) || '';
    
    try {
      // Submit to Zoho via direct hidden form POST
      const zohoForm = zohoFormRef.current;
      if (zohoForm) {
        const setField = (name, val) => {
          const el = zohoForm.querySelector(`[name="${name}"]`);
          if (el) el.value = val;
        };

        setField("Name_First", formData.firstName);
        setField("Name_Last", formData.lastName);
        setField("PhoneNumber_countrycode", `${formData.countryCode}${formData.phone.replace(/\D/g, "")}`);
        setField("Email", formData.email);
        setField("SingleLine1", formData.schoolName);
        setField("Address_Region", formData.state);
        setField("Address_City", formData.city);
        setField("Dropdown1", formData.grade || "-Select-");
        setField("Dropdown", formData.stream || "-Select-");
        setField("Dropdown2", formData.board || "-Select-");
        setField("utm_source", getUTMValue("utm_source"));
        setField("utm_medium", getUTMValue("utm_medium"));
        setField("utm_campaign", getUTMValue("utm_campaign"));
        setField("utm_term", getUTMValue("utm_term"));
        setField("utm_content", getUTMValue("utm_content"));
        setField("zf_referrer_name", "BEAT_Landingpage");
        setField("referrer_name", "BEAT_Landingpage");

        zohoForm.submit();
      }

      // Also save to backend as backup
      const API = process.env.REACT_APP_BACKEND_URL;
      fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: getFullPhoneNumber(),
          email: formData.email,
          school_name: formData.schoolName,
          state: formData.state,
          city: formData.city,
          grade: formData.grade,
          stream: formData.stream,
          board: formData.board,
          referrer_name: 'BEAT_Landingpage',
          utm_source: getUTMValue('utm_source'),
          utm_medium: getUTMValue('utm_medium'),
          utm_campaign: getUTMValue('utm_campaign'),
          utm_term: getUTMValue('utm_term'),
          utm_content: getUTMValue('utm_content'),
        }),
      }).catch(() => {});

      // Show success after brief delay for Zoho submission
      setTimeout(() => {
        setIsSubmitting(false);
        setIsSubmitted(true);
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: 'BEAT Registration',
            content_category: 'Education',
            city: formData.city,
            state: formData.state
          });
        }
      }, 2000);
    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-10 px-4" data-testid="form-success">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 size={32} className="text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-slate-900 mb-2">Registration Successful!</h3>
        <p className="text-slate-600 text-sm">
          We'll send you the test link on your selected date. Check your email for confirmation.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Hidden iframe target for Zoho form POST */}
      <iframe
        name="beat_zoho_submit_frame"
        style={{ position: "absolute", width: 0, height: 0, border: "none", overflow: "hidden" }}
        title="BEAT Zoho Submit"
        tabIndex={-1}
      />
      {/* Hidden Zoho form - direct POST to Zoho's endpoint */}
      <form
        ref={zohoFormRef}
        action={BEAT_ZOHO_FORM_ACTION}
        method="POST"
        target="beat_zoho_submit_frame"
        acceptCharset="UTF-8"
        encType="multipart/form-data"
        style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
        tabIndex={-1}
      >
        <input type="hidden" name="zf_referrer_name" value="" />
        <input type="hidden" name="zf_redirect_url" value="" />
        <input type="hidden" name="zc_gad" value="" />
        <input type="hidden" name="utm_source" value="" />
        <input type="hidden" name="utm_medium" value="" />
        <input type="hidden" name="utm_campaign" value="" />
        <input type="hidden" name="utm_term" value="" />
        <input type="hidden" name="utm_content" value="" />
        <input type="hidden" name="referrer_name" value="" />
        <input type="hidden" name="Name_First" value="" />
        <input type="hidden" name="Name_Last" value="" />
        <input type="hidden" name="PhoneNumber_countrycode" value="" />
        <input type="hidden" name="Email" value="" />
        <input type="hidden" name="SingleLine1" value="" />
        <input type="hidden" name="Address_Region" value="" />
        <input type="hidden" name="Address_City" value="" />
        <input type="hidden" name="Dropdown1" value="-Select-" />
        <input type="hidden" name="Dropdown" value="-Select-" />
        <input type="hidden" name="Dropdown2" value="-Select-" />
      </form>
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="registration-form" ref={formRef}>
        {submitError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600" data-testid="form-error">
            {submitError}
          </div>
        )}
        {/* Name Fields - Stack on mobile, side by side on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="form-label">First Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              className={`form-input-compact ${errors.firstName ? 'border-red-400' : ''}`}
              placeholder="First name"
              data-testid="input-first-name"
            />
            {errors.firstName && <p className="form-error-compact">{errors.firstName}</p>}
          </div>
          <div>
            <label className="form-label">Last Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              className={`form-input-compact ${errors.lastName ? 'border-red-400' : ''}`}
              placeholder="Last name"
              data-testid="input-last-name"
            />
            {errors.lastName && <p className="form-error-compact">{errors.lastName}</p>}
          </div>
        </div>

        {/* Phone with Country Code - Fixed layout */}
        <div>
          <label className="form-label">Phone <span className="text-red-500">*</span></label>
          <div className="phone-input-wrapper">
            <select
              name="countryCode"
              value={formData.countryCode}
              onChange={handleChange}
              className="country-code-select"
              data-testid="select-country-code"
            >
              {COUNTRY_CODES.map(cc => (
                <option key={cc.code} value={cc.code}>{cc.flag} {cc.code}</option>
              ))}
            </select>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className={`phone-number-input ${errors.phone ? 'border-red-400' : ''}`}
              placeholder="10-digit mobile"
              data-testid="input-phone"
            />
          </div>
          {errors.phone && <p className="form-error-compact">{errors.phone}</p>}
        </div>

        {/* Email - Full width on mobile */}
        <div>
          <label className="form-label">Email <span className="text-red-500">*</span></label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`form-input-compact ${errors.email ? 'border-red-400' : ''}`}
            placeholder="your@email.com"
            data-testid="input-email"
          />
          {errors.email && <p className="form-error-compact">{errors.email}</p>}
        </div>

        {/* School Name - Full Width */}
        <div>
          <label className="form-label">School Name <span className="text-red-500">*</span></label>
          <input
            type="text"
            name="schoolName"
            value={formData.schoolName}
            onChange={handleChange}
            className={`form-input-compact ${errors.schoolName ? 'border-red-400' : ''}`}
            placeholder="Your school name"
            data-testid="input-school"
          />
          {errors.schoolName && <p className="form-error-compact">{errors.schoolName}</p>}
        </div>

        {/* State - Full width on mobile, side by side on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="form-label">State <span className="text-red-500">*</span></label>
            <select
              name="state"
              value={formData.state}
              onChange={handleChange}
              className={`form-select-compact ${errors.state ? 'border-red-400' : ''}`}
              data-testid="select-state"
            >
              <option value="">Select State</option>
              {INDIAN_STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            {errors.state && <p className="form-error-compact">{errors.state}</p>}
          </div>
          <div>
            <label className="form-label">City <span className="text-red-500">*</span></label>
            <select
              name="city"
              value={formData.city}
              onChange={handleChange}
              className={`form-select-compact ${errors.city ? 'border-red-400' : ''}`}
              data-testid="select-city"
              disabled={!formData.state}
            >
              <option value="">{formData.state ? 'Select City' : 'Select State First'}</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
            {errors.city && <p className="form-error-compact">{errors.city}</p>}
          </div>
        </div>

        {/* Grade & Stream - Stack on mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="form-label">Grade/Class</label>
            <select
              name="grade"
              value={formData.grade}
              onChange={handleChange}
              className="form-select-compact"
              data-testid="select-grade"
            >
              {gradeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Stream <span className="text-red-500">*</span></label>
            <select
              name="stream"
              value={formData.stream}
              onChange={handleChange}
              className={`form-select-compact ${errors.stream ? 'border-red-400' : ''}`}
              data-testid="select-stream"
            >
              {streamOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {errors.stream && <p className="form-error-compact">{errors.stream}</p>}
          </div>
        </div>

        {/* Board - Full width */}
        <div>
          <label className="form-label">Board <span className="text-red-500">*</span></label>
          <select
            name="board"
            value={formData.board}
            onChange={handleChange}
            className={`form-select-compact ${errors.board ? 'border-red-400' : ''}`}
            data-testid="select-board"
          >
            {boardOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {errors.board && <p className="form-error-compact">{errors.board}</p>}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full cta-button py-3.5 text-base disabled:opacity-70 disabled:cursor-not-allowed mt-2"
          data-testid="form-submit-btn"
        >
          {isSubmitting ? (
            <>
              <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></span>
              Submitting...
            </>
          ) : (
            <>
              Register for BEAT
              <ArrowRight size={18} />
            </>
          )}
        </button>

        <p className="text-xs text-slate-500 text-center">
          By registering, you agree to receive communication about BEAT and Bower School programs.
        </p>
      </form>
    </>
  );
};

// Header Component
const Header = ({ isScrolled }) => {
  const scrollToForm = () => {
    document.getElementById('register-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className={`header py-4 ${isScrolled ? 'scrolled' : ''}`} data-testid="header">
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="logo-link">
          <img 
            src={BOWER_LOGO} 
            alt="Bower School of Entrepreneurship" 
            className="h-14 md:h-18 object-contain"
          />
        </a>
        <div className="flex items-center gap-4">
          <button 
            onClick={scrollToForm}
            className="cta-button small"
            data-testid="header-cta-btn"
          >
            Register for BEAT
          </button>
        </div>
      </div>
    </header>
  );
};

// Hero Section - Two column: text left, form right
const HeroSection = () => {
  return (
    <section className="hero-section-new pt-28 pb-16 md:pt-36 md:pb-24" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* Left Content */}
          <div className="animate-fade-in-up order-last lg:order-first min-w-0">
            {/* Badge */}
            <div className="hero-badge mb-6" data-testid="hero-badge">
              <Sparkles size={16} />
              <span>BEAT — Bower Entrepreneurial Aptitude Test</span>
            </div>

            {/* Subtitle line */}
            <p className="text-sm md:text-lg text-slate-500 mb-4 font-medium tracking-wide break-words">
              3-Year BBA in Entrepreneurship | Hyderabad | July 2026 Cohort
            </p>
            
            {/* Main Heading */}
            <h1 className="hero-headline-new" data-testid="hero-headline">
              Don't Just Take a Degree.{" "}
              <span className="accent">Build a Venture.</span>
            </h1>
            
            {/* Description */}
            <p className="text-base md:text-xl text-slate-600 mb-8 max-w-xl leading-relaxed">
              BEAT is your gateway to India's only 3-year undergraduate programme built entirely around entrepreneurship.
            </p>
            
            {/* Two-line CTA text */}
            <p className="text-sm md:text-lg text-slate-800 font-medium mb-2">
              Take the test. Unlock scholarships.
            </p>
            <p className="text-sm md:text-lg text-slate-800 font-medium mb-10">
              Join a community of founders-in-the-making.
            </p>
            
            {/* Info Tags/Pills */}
            <div className="flex flex-wrap gap-3 mb-8" data-testid="hero-tags">
              <div className="hero-info-tag">
                <GraduationCap size={18} className="text-[#2F4DC5]" />
                <span>3-Year BBA</span>
              </div>
              <div className="hero-info-tag">
                <MapPin size={18} className="text-[#2F4DC5]" />
                <span>Hyderabad</span>
              </div>
              <div className="hero-info-tag">
                <Award size={18} className="text-[#ED8548]" />
                <span>7 Scholarships</span>
              </div>
              <div className="hero-info-tag">
                <Calendar size={18} className="text-[#2F4DC5]" />
                <span>July 2026 — Limited Seats</span>
              </div>
            </div>

            {/* Scholarship Callout - below tags */}
            <div className="scholarship-callout" data-testid="scholarship-callout">
              <Award size={20} className="text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                <strong>You may qualify for one of 7 scholarships</strong> based on your BEAT score, academic profile, and background.
              </p>
            </div>
          </div>

          {/* Right - Form */}
          <div id="register-form" className="lg:pl-4 order-first lg:order-last min-w-0" data-testid="registration-form-container">
            <div className="hero-form-container overflow-hidden">
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Register for the BEAT Test
              </h3>
              <p className="text-sm text-slate-500 mb-5 pb-4 border-b border-slate-200">
                Fill in your details and our advisor will reach out to schedule your test and share scholarship details.
              </p>

              <RegistrationForm />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// What is BEAT Section - B-E-A-T cards
const WhatIsBeatSection = () => {
  const beatCards = [
    {
      letter: "B",
      title: "Bower",
      desc: "India's leading school of entrepreneurship based in Hyderabad."
    },
    {
      letter: "E",
      title: "Entrepreneurial",
      desc: "We test your instincts, not your rote knowledge."
    },
    {
      letter: "A",
      title: "Aptitude",
      desc: "Problem solving, opportunity sensing, creative thinking."
    },
    {
      letter: "T",
      title: "Test",
      desc: "Your gateway to Bower's UG programme and 7 scholarships."
    }
  ];

  return (
    <section className="py-16 md:py-20 bg-slate-50" data-testid="what-is-beat-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <span className="section-badge mb-4">WHAT IS BEAT?</span>
          <h2 className="section-title mb-4">
            Not Your Typical <span className="text-[#2F4DC5]">Entrance Exam</span>
          </h2>
          <p className="section-subtitle mx-auto text-center">
            BEAT doesn't test what you've memorised. It tests what you can think, build, and solve — the qualities every founder needs.
          </p>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {beatCards.map((card, index) => (
            <div key={index} className="beat-letter-card" data-testid={`beat-card-${card.letter}`}>
              <div className="beat-letter-icon">
                <span>{card.letter}</span>
              </div>
              <h3 className="font-bold text-lg text-slate-900 mb-2">{card.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{card.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Scholarships Section - 6 scholarship types
const ScholarshipsSection = () => {
  const scrollToForm = () => {
    document.getElementById('register-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scholarships = [
    {
      icon: Trophy,
      title: "Merit Scholarship",
      desc: "For high academic achievers with strong BEAT performance.",
      bgColor: "bg-amber-50",
      iconColor: "text-amber-500"
    },
    {
      icon: Dumbbell,
      title: "Sports Scholarship",
      desc: "For state or national-level athletes.",
      bgColor: "bg-indigo-50",
      iconColor: "text-indigo-400"
    },
    {
      icon: Building2,
      title: "Family Business Scholarship",
      desc: "For students from entrepreneurial families.",
      bgColor: "bg-emerald-50",
      iconColor: "text-emerald-500"
    },
    {
      icon: Cog,
      title: "Innovation Scholarship",
      desc: "For students who have already built projects or products.",
      bgColor: "bg-amber-50",
      iconColor: "text-amber-500"
    },
    {
      icon: Heart,
      title: "Women in Entrepreneurship",
      desc: "Supporting India's next generation of women founders.",
      bgColor: "bg-pink-50",
      iconColor: "text-pink-400"
    },
    {
      icon: Leaf,
      title: "Social Impact Scholarship",
      desc: "For students solving community problems.",
      bgColor: "bg-teal-50",
      iconColor: "text-teal-500"
    }
  ];

  return (
    <section className="py-16 md:py-20 bg-slate-50" data-testid="scholarships-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <span className="section-badge mb-4">SCHOLARSHIPS</span>
          <h2 className="section-title mb-4">
            Your Hard Work Deserves to Be <span className="text-[#2F4DC5]">Rewarded</span>
          </h2>
          <p className="section-subtitle mx-auto text-center">
            Bower offers 7 scholarship categories. Eligibility is determined by your BEAT Score, Academic Profile, and Student Background.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {scholarships.map((item, index) => (
            <div key={index} className="scholarship-card" data-testid={`scholarship-card-${index}`}>
              <div className={`scholarship-icon ${item.bgColor}`}>
                <item.icon size={24} className={item.iconColor} />
              </div>
              <h3 className="font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <button 
            onClick={scrollToForm}
            className="cta-button text-base px-8 py-4"
            data-testid="scholarships-cta-btn"
          >
            Check Your Eligibility — Take the BEAT Test
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

// BEAT Evaluates Section
const BeatEvaluatesSection = () => {
  const traits = [
    { icon: Lightbulb, title: "Problem-Solving Ability", desc: "How you approach and break down complex challenges", color: "primary" },
    { icon: Zap, title: "Creativity & Initiative", desc: "Your ability to think differently and take action", color: "secondary" },
    { icon: Target, title: "Curiosity About Real-World Problems", desc: "Interest in solving problems that matter", color: "primary" },
    { icon: Users, title: "Leadership Mindset", desc: "Your potential to inspire and lead teams", color: "secondary" },
    { icon: TrendingUp, title: "Business Thinking", desc: "Understanding of value creation and markets", color: "primary" },
    { icon: Brain, title: "Personality Profile", desc: "OCEAN assessment of your entrepreneurial personality traits", color: "secondary" },
  ];

  return (
    <section className="py-16 md:py-20 bg-white" data-testid="beat-evaluates-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <span className="section-badge mb-4">What BEAT Measures</span>
          <h2 className="section-title mb-4">
            Is Your Child Built to Be an <span className="text-[#2F4DC5]">Entrepreneur?</span>
          </h2>
          <p className="section-subtitle mx-auto">
            BEAT evaluates the traits that define successful founders — not memorization, but mindset.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {traits.map((trait, index) => (
            <div key={index} className="benefit-card card-hover" data-testid={`trait-card-${index}`}>
              <div className={`benefit-icon ${trait.color}`}>
                <trait.icon size={28} />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">{trait.title}</h3>
              <p className="text-slate-600">{trait.desc}</p>
            </div>
          ))}
        </div>
        
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 md:p-10 text-center">
          <p className="text-xl md:text-2xl font-semibold text-slate-800 mb-2">
            "This is not a memory test.
          </p>
          <p className="text-xl md:text-2xl font-semibold text-[#2F4DC5]">
            It is a mindset test."
          </p>
        </div>
      </div>
    </section>
  );
};

// Assessment Modules Section
const AssessmentModulesSection = () => {
  const modules = [
    {
      icon: Lightbulb,
      number: "01",
      title: "Logical Reasoning & Analytical Thinking",
      type: "Quiz",
      desc: "Test your ability to think logically, analyze patterns, and solve complex problems systematically.",
      topics: ["Pattern recognition", "Data interpretation", "Logical deduction", "Critical analysis"]
    },
    {
      icon: Rocket,
      number: "02", 
      title: "Creativity & Innovation Case Studies",
      type: "Quiz",
      desc: "Evaluate your creative thinking through real-world innovation scenarios and business case studies.",
      topics: ["Innovative solutions", "Design thinking", "Out-of-box ideas", "Case analysis"]
    },
    {
      icon: Briefcase,
      number: "03",
      title: "Business Scenario Problem Solving",
      type: "Quiz",
      desc: "Assess your business acumen through practical scenarios requiring strategic decision-making.",
      topics: ["Market analysis", "Decision making", "Resource allocation", "Strategy planning"]
    }
  ];

  return (
    <section className="py-16 md:py-20 bg-gradient-section" data-testid="assessment-modules-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <span className="section-badge mb-4">Test Format</span>
          <h2 className="section-title mb-4">
            Assessment <span className="text-[#ED8548]">Modules</span>
          </h2>
          <p className="section-subtitle mx-auto">
            BEAT consists of three comprehensive modules designed to evaluate your entrepreneurial potential, creativity, and problem-solving abilities.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-8">
          {modules.map((module, index) => (
            <div 
              key={index} 
              className="module-card bg-white border border-slate-200 rounded-2xl p-8 relative overflow-hidden card-hover"
              data-testid={`module-card-${index}`}
            >
              {/* Module Number */}
              <div className="absolute top-6 right-6 text-6xl font-bold text-slate-100 select-none">
                {module.number}
              </div>
              
              {/* Icon */}
              <div className={`benefit-icon ${index % 2 === 0 ? 'primary' : 'secondary'} mb-6 relative z-10`}>
                <module.icon size={28} />
              </div>
              
              {/* Type Badge */}
              <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full mb-4 bg-blue-100 text-blue-700">
                {module.type}
              </span>
              
              {/* Title */}
              <h3 className="font-semibold text-xl text-slate-900 mb-3 relative z-10">
                {module.title}
              </h3>
              
              {/* Description */}
              <p className="text-slate-600 mb-5 relative z-10">
                {module.desc}
              </p>
              
              {/* Topics */}
              <div className="space-y-2 relative z-10">
                <p className="text-sm font-medium text-slate-500 mb-2">Topics covered:</p>
                <div className="flex flex-wrap gap-2">
                  {module.topics.map((topic, topicIndex) => (
                    <span 
                      key={topicIndex}
                      className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Sample Test Paper Download */}
        <div className="mt-12 bg-[#2F4DC5] rounded-2xl p-8 md:p-10 text-center text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative z-10">
            <FileText size={40} className="mx-auto mb-4 text-white/80" />
            <h3 className="text-2xl font-bold mb-3">Download Sample BEAT Test Paper</h3>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Prepare for your BEAT assessment with our sample test paper. Get familiar with the question format and difficulty level.
            </p>
            <a 
              href="https://drive.google.com/file/d/1OJJMCFyPCXTLpqedz0flElzRJe3bwOoF/view?usp=drive_link"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#2F4DC5] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-colors"
              data-testid="sample-paper-download-btn"
            >
              <Download size={20} />
              Download Sample Paper
            </a>
          </div>
        </div>
        
        {/* Sample Report Preview */}
        <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-8 md:p-10">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Get Your Detailed BEAT Report
              </h3>
              <p className="text-slate-600 mb-6">
                After completing the assessment, you'll receive a comprehensive report including:
              </p>
              <ul className="space-y-3">
                {[
                  "Entrepreneurial Quotient Score",
                  "Personality Profile (OCEAN Big Five)",
                  "Strengths & Areas for Development",
                  "Personalized Recommendations",
                  "Comparison with Successful Founder Profiles"
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 size={20} className="text-green-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
              <div className="text-center mb-6">
                <h4 className="font-semibold text-slate-800 mb-2">Sample Personality Profile</h4>
                <p className="text-sm text-slate-500">OCEAN (Big Five) Assessment Result</p>
              </div>
              {/* Personality Bars */}
              <div className="space-y-4">
                {[
                  { trait: "Openness", score: 62, color: "bg-emerald-500" },
                  { trait: "Conscientiousness", score: 68, color: "bg-blue-500" },
                  { trait: "Extraversion", score: 44, color: "bg-rose-400" },
                  { trait: "Agreeableness", score: 56, color: "bg-amber-400" },
                  { trait: "Neuroticism", score: 46, color: "bg-purple-400" }
                ].map((item, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{item.trait}</span>
                      <span className="font-medium text-slate-800">{item.score}%</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${item.color} rounded-full transition-all duration-1000`}
                        style={{ width: `${item.score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Why Take BEAT Section
const WhyBeatSection = () => {
  const benefits = [
    { icon: GraduationCap, title: "Admission Opportunity", desc: "Priority admission pathway to Bower's 3-year UG Program in Business Entrepreneurship" },
    { icon: Award, title: "Scholarships", desc: "Top performers receive merit-based scholarships for the undergraduate program" },
    { icon: Rocket, title: "Startup Ecosystem Access", desc: "Connect with mentors, investors, and fellow aspiring entrepreneurs" },
    { icon: Sparkles, title: "Early Exposure", desc: "Get introduced to entrepreneurship concepts and real-world business challenges" },
    { icon: MessageCircle, title: "Founder Masterclasses", desc: "Exclusive invitation to workshops conducted by successful founders and VCs" },
    { icon: Users, title: "Mentorship Network", desc: "Access to a community of entrepreneurs and industry experts" },
  ];

  return (
    <section className="py-16 md:py-20 bg-gradient-section" data-testid="why-beat-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="section-badge mb-4">Benefits</span>
            <h2 className="section-title mb-6">
              Why Students Take <span className="text-[#ED8548]">BEAT</span>
            </h2>
            <p className="section-subtitle mb-10">
              BEAT is more than just an aptitude test — it's your gateway to India's most innovative entrepreneurship education.
            </p>
            
            <div className="space-y-5">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4" data-testid={`benefit-item-${index}`}>
                  <div className="flex-shrink-0 w-12 h-12 bg-white rounded-xl shadow-sm border border-slate-100 flex items-center justify-center">
                    <benefit.icon size={22} className="text-[#2F4DC5]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">{benefit.title}</h4>
                    <p className="text-slate-600 text-sm">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <img 
              src={IMAGES.studentsGroup} 
              alt="Students collaborating at Bower School" 
              className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              style={{ maxHeight: '500px', objectPosition: 'center 60%' }}
            />
            <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-5 hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 size={24} className="text-green-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">500+</p>
                  <p className="text-sm text-slate-500">Students Registered</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// What Makes Bower Different Section
const BowerDifferentSection = () => {
  const differentiators = [
    { icon: Rocket, title: "Build Real Startups", desc: "Launch actual ventures during your degree, not just theoretical case studies" },
    { icon: Users, title: "Learn from Founders & VCs", desc: "Direct mentorship from entrepreneurs and venture capitalists who've built successful companies" },
    { icon: Briefcase, title: "Real Business Challenges", desc: "Work on live projects and solve actual business problems facing startups" },
    { icon: Target, title: "Startup Ecosystem Exposure", desc: "Connect with incubators, accelerators, and the broader startup community" },
    { icon: Zap, title: "Execution Over Theory", desc: "Focus on building, launching, and iterating — not just reading about it" },
  ];

  return (
    <section className="py-16 md:py-20 bg-white" data-testid="bower-different-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <span className="section-badge mb-4">Why Bower</span>
          <h2 className="section-title mb-4">
            What Makes Bower <span className="text-[#2F4DC5]">Different</span>
          </h2>
          <p className="section-subtitle mx-auto">
            This is not a traditional business degree — it's a builder-focused program designed for future founders.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {differentiators.map((item, index) => (
            <div 
              key={index} 
              className={`benefit-card card-hover ${index === 0 ? 'lg:col-span-2' : ''}`}
              data-testid={`differentiator-card-${index}`}
            >
              <div className="benefit-icon secondary">
                <item.icon size={28} />
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Steps Section
const StepsSection = () => {
  const steps = [
    { step: 1, title: "Register for BEAT", desc: "Fill out the registration form and select your preferred test date" },
    { step: 2, title: "Take the Online Test", desc: "Complete the 60-90 minute aptitude assessment from anywhere" },
    { step: 3, title: "Get Shortlisted", desc: "Top performers are invited for an interaction with the Bower team" },
    { step: 4, title: "Secure Your Spot", desc: "Receive scholarship opportunities and admission pathway to Bower UG Program" },
  ];

  return (
    <section className="py-16 md:py-20 bg-gradient-section" data-testid="steps-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-16">
          <span className="section-badge mb-4">Process</span>
          <h2 className="section-title mb-4">
            What Happens After <span className="text-[#ED8548]">BEAT?</span>
          </h2>
          <p className="section-subtitle mx-auto">
            Your journey from registration to becoming part of the Bower community
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((item, index) => (
            <div key={index} className="step-card card-hover relative" data-testid={`step-card-${index}`}>
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/4 -right-3 text-slate-300">
                  <ArrowRight size={24} />
                </div>
              )}
              <div className="step-number">{item.step}</div>
              <h3 className="font-semibold text-lg text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// For Parents Section
const ParentsSection = () => {
  const trustPoints = [
    { icon: TrendingUp, title: "Entrepreneurship as Career Path", desc: "In today's economy, entrepreneurial skills are essential regardless of career choice. BEAT and Bower prepare students for the future of work." },
    { icon: BookOpen, title: "Structured Learning", desc: "A comprehensive 3-year curriculum designed by entrepreneurs and educators, balancing theory with hands-on experience." },
    { icon: Users, title: "Mentorship Ecosystem", desc: "Students learn directly from successful founders, investors, and industry experts who guide their entrepreneurial journey." },
    { icon: Briefcase, title: "Industry Exposure", desc: "Regular interactions with startups, corporate partners, and the broader business community provide real-world context." },
  ];

  return (
    <section className="py-16 md:py-20 bg-white" data-testid="parents-section">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-stretch">
          <div className="order-2 lg:order-1 flex flex-col gap-4">
            <img 
              src={IMAGES.parentsTop} 
              alt="Students in a Bower learning session" 
              className="rounded-2xl shadow-xl w-full object-cover flex-1"
              style={{ objectPosition: 'center bottom', minHeight: '0' }}
            />
            <img 
              src={IMAGES.parentsBottom} 
              alt="Bower School students receiving awards" 
              className="rounded-2xl shadow-xl w-full object-cover flex-1"
              style={{ objectPosition: 'center 75%', minHeight: '0' }}
            />
          </div>
          
          <div className="order-1 lg:order-2">
            <span className="section-badge mb-4">For Parents</span>
            <h2 className="section-title mb-6">
              Building a <span className="text-[#2F4DC5]">Strong Foundation</span> for Your Child's Future
            </h2>
            <p className="section-subtitle mb-10">
              We understand your concerns. Here's why parents across India trust Bower for their children's entrepreneurial education.
            </p>
            
            <div className="grid gap-6">
              {trustPoints.map((point, index) => (
                <div key={index} className="trust-card" data-testid={`trust-point-${index}`}>
                  <div className="trust-icon">
                    <point.icon size={28} className="text-[#2F4DC5]" />
                  </div>
                  <h4 className="font-semibold text-lg text-slate-900 mb-2">{point.title}</h4>
                  <p className="text-slate-600">{point.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Test Details Section
const TestDetailsSection = () => {
  const scrollToForm = () => {
    document.getElementById('register-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="py-16 md:py-20 bg-gradient-section" data-testid="test-details-section">
      <div className="max-w-5xl mx-auto px-6">
        <div className="details-card">
          <div className="text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">BEAT Test Details</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
              <div className="text-center">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Monitor size={24} />
                </div>
                <p className="text-white/70 text-xs mb-1">Mode</p>
                <p className="font-semibold text-sm">Online Test</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Clock size={24} />
                </div>
                <p className="text-white/70 text-xs mb-1">Duration</p>
                <p className="font-semibold text-sm">60–90 mins</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <GraduationCap size={24} />
                </div>
                <p className="text-white/70 text-xs mb-1">Eligibility</p>
                <p className="font-semibold text-sm">Class 11, 12</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <CreditCard size={24} />
                </div>
                <p className="text-white/70 text-xs mb-1">Registration Fee</p>
                <p className="font-semibold text-sm">₹1,500</p>
              </div>
            </div>
            
            <div className="bg-white/10 rounded-xl p-5 mb-8">
              <p className="text-white/90 text-sm">
                <Calendar size={16} className="inline mr-2" />
                <strong>Flexible Test Dates:</strong> Test link will be shared with you via email after successful registration and payment.
              </p>
            </div>
            
            <button 
              onClick={scrollToForm}
              className="cta-button bg-white text-[#2F4DC5] hover:bg-slate-100"
              data-testid="test-details-cta-btn"
            >
              Register Now
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
        
        {/* Payment & Next Steps */}
        <div className="mt-12 grid md:grid-cols-2 gap-8">
          {/* Payment Instructions */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <CreditCard size={24} className="text-[#2F4DC5]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">Payment Instructions</h3>
            </div>
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-[#2F4DC5] text-white rounded-full flex items-center justify-center text-sm font-medium">1</span>
                <span className="text-slate-600">Fill out the registration form with your details</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-[#2F4DC5] text-white rounded-full flex items-center justify-center text-sm font-medium">2</span>
                <span className="text-slate-600">You will be redirected to Razorpay's secure payment gateway</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-[#2F4DC5] text-white rounded-full flex items-center justify-center text-sm font-medium">3</span>
                <span className="text-slate-600">Enter your payment details and complete the transaction</span>
              </li>
            </ol>
          </div>
          
          {/* What Happens Next */}
          <div className="bg-white rounded-2xl border border-slate-200 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 size={24} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">What Happens Next?</h3>
            </div>
            <p className="text-slate-600 mb-4">Once your payment is successful:</p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-600">You will receive an email confirmation with your payment receipt</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-600">Details about the BEAT Test, including schedule and guidelines, will be sent to your registered email</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-600">Test link and schedule will be shared via email</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Important Notes */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-6 md:p-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <AlertCircle size={20} className="text-amber-600" />
            </div>
            <div>
              <h4 className="font-semibold text-amber-900 mb-3">Important Notes</h4>
              <ul className="space-y-2 text-amber-800 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  Please ensure that your contact details (email and phone number) are correct
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  Save your payment receipt for future reference
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  <strong>The test fee is non-refundable</strong>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">•</span>
                  For any issues, contact our admissions team at <a href="mailto:ug@bowerschool.com" className="text-[#2F4DC5] font-medium underline">ug@bowerschool.com</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      q: "What is BEAT?",
      a: "BEAT (Bower Entrepreneurial Aptitude Test) is an assessment designed to identify students with founder mindset, creativity, and problem-solving ability. It evaluates entrepreneurial thinking rather than academic knowledge."
    },
    {
      q: "Who should take the test?",
      a: "BEAT is designed for Class 11 & 12 students, gap year students, and anyone interested in exploring entrepreneurship as a career path. No prior business knowledge is required."
    },
    {
      q: "What is the test fee?",
      a: "The regular test fee is ₹3,000. However, we're offering an Early Bird discount of 50% off, so you can register for just ₹1,500. Please note that the fee is non-refundable."
    },
    {
      q: "What is the test format?",
      a: "BEAT consists of three modules: (1) Logical Reasoning and Analytical Thinking, (2) Creativity and Innovation Case Studies, and (3) Business Scenario Problem Solving. The test duration is 60-90 minutes."
    },
    {
      q: "Is prior business knowledge required?",
      a: "No! BEAT tests your mindset, not your business knowledge. It evaluates how you think, solve problems, and approach challenges — traits that define successful entrepreneurs."
    },
    {
      q: "Is the test online or offline?",
      a: "BEAT is conducted entirely online. After registration and payment, the test link and schedule will be shared with you via email."
    },
    {
      q: "How do I pay for the test?",
      a: "After submitting the registration form, you will be redirected to Razorpay's secure payment gateway. Enter your payment details and complete the transaction. You'll receive an email confirmation with your receipt."
    },
    {
      q: "What happens after the test?",
      a: "Top performers are shortlisted for an interaction with the Bower team. Based on performance, students may receive admission priority, scholarships, and invitations to founder masterclasses. You'll also receive a detailed BEAT Report with your scores and personality profile."
    },
    {
      q: "Is there a sample test paper available?",
      a: "Yes! You can download a sample BEAT test paper from our website to get familiar with the question format and difficulty level before taking the actual test."
    },
    {
      q: "What is Bower School of Entrepreneurship?",
      a: "Bower offers a 3-year Undergraduate Program in Business Entrepreneurship where students build real startups during the degree, learn from founders and VCs, and focus on execution rather than just theory."
    }
  ];

  return (
    <section className="py-16 md:py-20 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="section-badge mb-4">FAQs</span>
          <h2 className="section-title">
            Frequently Asked <span className="text-[#2F4DC5]">Questions</span>
          </h2>
        </div>
        
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`faq-${index}`}
              className="faq-item"
              data-testid={`faq-item-${index}`}
            >
              <AccordionTrigger className="px-6 py-4 hover:no-underline text-left font-semibold text-slate-900">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-4 text-slate-600">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  const scrollToForm = () => {
    document.getElementById('register-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="final-cta-section py-24 md:py-32" data-testid="final-cta-section">
      <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
          The Next Generation of Entrepreneurs<br/>
          <span className="text-[#ED8548]">Won't Wait Until 25.</span>
        </h2>
        <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
          Start early. Discover your entrepreneurial potential with BEAT and take the first step towards building your future.
        </p>
        <button 
          onClick={scrollToForm}
          className="cta-button animate-pulse-glow text-lg px-10 py-5"
          data-testid="final-cta-btn"
        >
          Register for BEAT
          <ArrowRight size={22} />
        </button>
        <p className="text-white/50 mt-6 text-sm">
          Limited seats available for Hyderabad Edition 2026
        </p>
      </div>
    </section>
  );
};

// Sticky CTA Component
const StickyCTA = ({ show }) => {
  const scrollToForm = () => {
    document.getElementById('register-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!show) return null;

  return (
    <>
      {/* Mobile Sticky CTA */}
      <div className="sticky-cta-mobile md:hidden" data-testid="sticky-cta-mobile">
        <div>
          <p className="font-semibold text-slate-900 text-sm">BEAT Test</p>
          <p className="text-xs text-slate-500">Limited Seats</p>
        </div>
        <button 
          onClick={scrollToForm}
          className="cta-button small"
          data-testid="sticky-cta-mobile-btn"
        >
          Register
        </button>
      </div>
    </>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="footer py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div>
            <img 
              src={BOWER_LOGO} 
              alt="Bower School" 
              className="h-12 object-contain brightness-0 invert mb-4"
            />
            <p className="text-white/60 text-sm leading-relaxed mb-4">Building the next generation of industry disruptors.</p>
            <div className="flex flex-col gap-2 text-white/60 text-sm">
              <a href="mailto:ug@bowerschool.com" className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail size={14} />
                ug@bowerschool.com
              </a>
              <span className="flex items-center gap-2">
                <MapPin size={14} />
                Hyderabad, India
              </span>
            </div>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Programs</h4>
            <nav className="flex flex-col gap-3">
              <a href="/beat" className="text-white/60 text-sm hover:text-white transition-colors">BEAT Test</a>
              <a href="/gitam" className="text-white/60 text-sm hover:text-white transition-colors">GITAM x Bower BBA</a>
              <a href="/seed" className="text-white/60 text-sm hover:text-white transition-colors">SEED for Schools</a>
              <a href="https://bowerschool.com/" target="_blank" rel="noopener noreferrer" className="text-white/60 text-sm hover:text-white transition-colors">3-Year BBA Programme</a>
            </nav>
          </div>

          {/* Events */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Events</h4>
            <nav className="flex flex-col gap-3">
              <a href="/events" className="text-white/60 text-sm hover:text-white transition-colors">All Events</a>
              <a href="/admin/events" className="text-white/60 text-sm hover:text-white transition-colors">Manage Events</a>
            </nav>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-4">Company</h4>
            <nav className="flex flex-col gap-3">
              <a href="https://bowerschool.com/" target="_blank" rel="noopener noreferrer" className="text-white/60 text-sm hover:text-white transition-colors">Home</a>
              <a href="https://bowerschool.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-white/60 text-sm hover:text-white transition-colors">Terms of Service</a>
              <a href="https://bowerschool.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-white/60 text-sm hover:text-white transition-colors">Privacy Policy</a>
              <a href="https://bowerschool.com/refund-policy" target="_blank" rel="noopener noreferrer" className="text-white/60 text-sm hover:text-white transition-colors">Refund Policy</a>
            </nav>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 text-center text-white/40 text-sm">
          <p>&copy; 2025 Bower School of Entrepreneurship. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// BEAT Landing Page Component (extracted from App)
const BeatLandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showStickyCTA, setShowStickyCTA] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowStickyCTA(window.scrollY > 600);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="App" data-testid="beat-landing-page">
      <Header isScrolled={isScrolled} />
      <main>
        <HeroSection />
        <WhatIsBeatSection />
        <ScholarshipsSection />
        <BeatEvaluatesSection />
        <AssessmentModulesSection />
        <WhyBeatSection />
        <BowerDifferentSection />
        <StepsSection />
        <ParentsSection />
        <TestDetailsSection />
        <FAQSection />
        <FinalCTASection />
      </main>
      <Footer />
      <StickyCTA show={showStickyCTA} />
      <div className="noise-overlay" />
    </div>
  );
};

// Main App Component - Router
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/beat" element={<BeatLandingPage />} />
        <Route path="/events" element={<EventsListingPage />} />
        <Route path="/events/:id" element={<EventDetailPage />} />
        <Route path="/admin/events" element={<AdminEventsPage />} />
        <Route path="/seed" element={<SeedPartnershipPage />} />
        <Route path="/gitam" element={<GitamBBAPage />} />
        <Route path="/ug" element={<UGProgramPage />} />
        <Route path="/founder-os" element={<FounderOSPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
